import { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { appId } from '../constants';

const EventChangesView = () => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    canceled: true,
    title: true,
    venue: true,
    date: true,
    other: true
  });

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'eventChanges'),
      orderBy('timestamp', 'desc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setChanges(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const toggleFilter = (key) => setFilters(prev => ({ ...prev, [key]: !prev[key] }));

  const getTags = (change) => {
    const tags = [];
    const c = change.changes || {};
    if (c.isCanceled && c.isCanceled.new) tags.push({ id: 'canceled', label: 'Canceled', color: 'bg-red-100 text-red-700 border-red-200' });
    if (c.title) tags.push({ id: 'title', label: 'Title Change', color: 'bg-blue-100 text-blue-700 border-blue-200' });
    if (c.venue) tags.push({ id: 'venue', label: 'Venue Change', color: 'bg-purple-100 text-purple-700 border-purple-200' });
    if (c.startDate) tags.push({ id: 'date', label: 'Date Change', color: 'bg-amber-100 text-amber-700 border-amber-200' });
    
    // Any other change that is not covered above
    const hasOther = Object.keys(c).some(k => !['isCanceled', 'title', 'venue', 'startDate'].includes(k)) || (c.isCanceled && !c.isCanceled.new);
    if (hasOther) {
      tags.push({ id: 'other', label: 'Other', color: 'bg-zinc-100 text-zinc-700 border-zinc-200' });
    }
    return tags;
  };

  const filteredChanges = useMemo(() => {
    return changes.filter(change => {
      const tags = getTags(change);
      if (tags.length === 0) return true; // Show if no tags somehow
      return tags.some(tag => filters[tag.id]);
    });
  }, [changes, filters]);

  if (loading) return <div className="p-8 text-zinc-500 font-medium">Loading changes...</div>;

  return (
    <div className="h-full w-full bg-white overflow-y-auto p-4 md:p-8 custom-scrollbar">
      <div className="max-w-4xl mx-auto flex flex-col gap-6">
        <div>
          <h1 className="text-3xl font-black text-zinc-900 tracking-tighter uppercase">Concert Changes</h1>
          <p className="text-zinc-500 mt-1 font-medium">A log of all updates and cancellations from the Songkick concert database.</p>
          
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6 p-4 bg-zinc-50 border border-zinc-200 rounded-xl">
            <span className="text-xs font-black uppercase tracking-widest text-zinc-400 w-full sm:w-auto">Filter By:</span>
            <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-zinc-700 select-none">
              <input type="checkbox" checked={filters.canceled} onChange={() => toggleFilter('canceled')} className="w-4 h-4 rounded text-zinc-900 focus:ring-zinc-900 border-zinc-300 cursor-pointer" />
              Canceled
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-zinc-700 select-none">
              <input type="checkbox" checked={filters.title} onChange={() => toggleFilter('title')} className="w-4 h-4 rounded text-zinc-900 focus:ring-zinc-900 border-zinc-300 cursor-pointer" />
              Title Change
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-zinc-700 select-none">
              <input type="checkbox" checked={filters.venue} onChange={() => toggleFilter('venue')} className="w-4 h-4 rounded text-zinc-900 focus:ring-zinc-900 border-zinc-300 cursor-pointer" />
              Venue Change
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-zinc-700 select-none">
              <input type="checkbox" checked={filters.date} onChange={() => toggleFilter('date')} className="w-4 h-4 rounded text-zinc-900 focus:ring-zinc-900 border-zinc-300 cursor-pointer" />
              Date Change
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-zinc-700 select-none">
              <input type="checkbox" checked={filters.other} onChange={() => toggleFilter('other')} className="w-4 h-4 rounded text-zinc-900 focus:ring-zinc-900 border-zinc-300 cursor-pointer" />
              Other
            </label>
          </div>
        </div>
        
        {filteredChanges.length === 0 ? (
          <div className="text-zinc-500 font-medium">No recent changes found matching your filters.</div>
        ) : (
          <div className="flex flex-col gap-4">
            {filteredChanges.map(change => {
              let eventLink = null;
              if (change.link) {
                const urlMatch = change.link.match(/https?:\/\/[^\s]+/);
                if (urlMatch) eventLink = urlMatch[0];
              }
              if (!eventLink && change.concertId && change.concertId.startsWith('sk-')) {
                const idPart = change.concertId.split('-')[1];
                if (/^\d+$/.test(idPart)) {
                  eventLink = `https://www.songkick.com/concerts/${idPart}`;
                }
              }

              const tags = getTags(change);

              return (
                <div key={change.id} className="border border-zinc-200 p-4 rounded-xl flex flex-col gap-3 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div className="flex flex-col min-w-0 flex-1">
                      {eventLink ? (
                        <a href={eventLink} target="_blank" rel="noopener noreferrer" className="font-bold text-blue-600 hover:underline text-sm md:text-base break-all leading-tight">
                          {eventLink}
                        </a>
                      ) : (
                        <span className="font-bold text-zinc-900 text-sm md:text-base break-all leading-tight">
                          Event ID: {change.concertId}
                        </span>
                      )}
                      <span className="text-sm text-zinc-500 font-bold mt-2">
                        {change.artist} • {change.city}
                      </span>
                      
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {tags.map(tag => (
                            <span key={tag.id} className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded border ${tag.color}`}>
                              {tag.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-start sm:items-end shrink-0 bg-zinc-50 px-3 py-2 rounded-lg border border-zinc-100">
                      <span className="text-[10px] font-black uppercase text-zinc-400 tracking-widest whitespace-nowrap">
                        Changes Noticed On
                      </span>
                      <span className="text-xs md:text-sm font-bold text-zinc-700 whitespace-nowrap mt-0.5">
                        {change.timestamp?.toDate ? change.timestamp.toDate().toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Just now'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-zinc-50 rounded-lg p-3 flex flex-col gap-3 border border-zinc-100 mt-2">
                    {Object.entries(change.changes || {}).map(([key, vals]) => (
                      <div key={key} className="flex flex-col gap-1.5 text-sm border-b border-zinc-200/60 pb-3 last:border-0 last:pb-0">
                        <span className="font-black text-zinc-800 uppercase tracking-widest text-[10px]">{key}</span>
                        <div className="flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-3">
                          <div className="flex-1 min-w-0 bg-red-50/50 text-red-900 p-2.5 rounded-md border border-red-100">
                            <span className="text-[9px] font-black uppercase tracking-wider block mb-1 text-red-500/80">Previous Value</span>
                            <div className="break-words whitespace-pre-wrap font-medium">{String(vals.old)}</div>
                          </div>
                          <div className="hidden sm:flex items-center justify-center text-zinc-300 font-black">→</div>
                          <div className="flex-1 min-w-0 bg-emerald-50/50 text-emerald-900 p-2.5 rounded-md border border-emerald-100">
                            <span className="text-[9px] font-black uppercase tracking-wider block mb-1 text-emerald-600/80">New Value</span>
                            <div className="break-words whitespace-pre-wrap font-bold">{String(vals.new)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventChangesView;

